import React, { useId, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

/**
 * CollapsibleSection - reusable expand/collapse disclosure.
 *
 * Presentation only: it renders whatever children it is given and keeps its
 * own open/closed state. It never transforms or filters the content.
 *
 * @param {string} title - Section heading
 * @param {React.ReactNode} eyebrow - Optional small label rendered before the title
 * @param {React.ReactNode} meta - Optional right-aligned metadata (counts, chips)
 * @param {boolean} defaultOpen - Whether the section starts expanded
 * @param {boolean} open - Controlled open state (optional)
 * @param {Function} onToggle - Called with the next open state when toggled
 */
export default function CollapsibleSection({
  title,
  eyebrow = null,
  meta = null,
  defaultOpen = true,
  open: controlledOpen,
  onToggle,
  children,
  className = "",
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const panelId = useId();

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledOpen(next);
    if (onToggle) onToggle(next);
  };

  return (
    <div className={`rs-collapsible ${isOpen ? "is-open" : ""} ${className}`}>
      <button
        type="button"
        className="rs-collapsible-trigger"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <motion.span
          className="rs-collapsible-chevron"
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
        >
          <ChevronRight size={15} />
        </motion.span>

        <span className="rs-collapsible-heading">
          {eyebrow && <span className="rs-collapsible-eyebrow">{eyebrow}</span>}
          <span className="rs-collapsible-title">{title}</span>
        </span>

        {meta && <span className="rs-collapsible-meta">{meta}</span>}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
            className="rs-collapsible-panel"
          >
            <div className="rs-collapsible-body">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
